"use client"

import { useState, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import Image from "next/image"
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react"
import { HttpTypes } from "@medusajs/types"
import WishlistButton from "@modules/common/components/wishlist-button"

type ImageGalleryProps = {
  images: HttpTypes.StoreProductImage[]
  discountPercent?: number
  customizable?: boolean
  productId?: string
  productTitle?: string
}

const ImageGallery = ({
  images,
  discountPercent,
  customizable,
  productId,
  productTitle,
}: ImageGalleryProps) => {
  const imageUrls = (images ?? []).filter((img) => !!img.url)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isZoomOpen, setIsZoomOpen] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => setIsMounted(true), [])

  // Reset selected image when variant changes
  useEffect(() => {
    if (images[0]?.url) setSelectedIndex(0)
  }, [images])

  const selectedImage = imageUrls[selectedIndex]?.url ?? ""

  const openZoom = useCallback(() => setIsZoomOpen(true), [])
  const closeZoom = useCallback(() => setIsZoomOpen(false), [])

  const prevImage = useCallback(() => {
    setSelectedIndex((i) => (i === 0 ? imageUrls.length - 1 : i - 1))
  }, [imageUrls.length])

  const nextImage = useCallback(() => {
    setSelectedIndex((i) => (i === imageUrls.length - 1 ? 0 : i + 1))
  }, [imageUrls.length])

  // Keyboard + scroll-lock when zoom is open
  useEffect(() => {
    if (!isZoomOpen) return
    document.body.style.overflow = "hidden"
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeZoom()
      if (e.key === "ArrowLeft") prevImage()
      if (e.key === "ArrowRight") nextImage()
    }
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("keydown", handleKey)
      document.body.style.overflow = ""
    }
  }, [isZoomOpen, closeZoom, prevImage, nextImage])

  return (
    <>
      {/* ── Panel Layout ──────────────────────────────── */}
      <div className="flex flex-col small:flex-row small:gap-3">

        {/* Thumbnail strip — left on desktop, scrollable row on mobile */}
        {imageUrls.length > 1 && (
          <div className="order-2 small:order-1 flex small:flex-col gap-2 small:w-[76px] mt-3 small:mt-0 overflow-x-auto small:overflow-y-auto scrollbar-hide small:max-h-[520px]">
            {imageUrls.slice(0, 6).map((img, idx) => (
              <button
                key={img.id ?? idx}
                type="button"
                onClick={() => setSelectedIndex(idx)}
                className={`
                  flex-shrink-0 w-16 small:w-full aspect-square rounded-md overflow-hidden border-2 bg-white transition-all
                  ${selectedIndex === idx
                    ? "border-brand-navy ring-1 ring-brand-navy"
                    : "border-gray-200 hover:border-gray-400"
                  }
                `}
                aria-label={`View image ${idx + 1}`}
              >
                <Image
                  src={img.url!}
                  alt={`Product view ${idx + 1}`}
                  width={76}
                  height={76}
                  className="w-full h-full object-contain p-1"
                />
              </button>
            ))}
          </div>
        )}

        {/* Main image */}
        <div className="order-1 small:order-2 flex-1 relative bg-[#f8f8f8] rounded-lg overflow-hidden aspect-square">

          {/* Badges — top left */}
          {(discountPercent || customizable) && (
            <div className="absolute top-3 left-3 flex gap-2 z-10">
              {discountPercent != null && discountPercent > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                  -{discountPercent}%
                </span>
              )}
              {customizable && (
                <span className="bg-white text-brand-navy text-xs font-semibold px-3 py-1 rounded-full border border-brand-navy shadow-sm">
                  Customisable
                </span>
              )}
            </div>
          )}

          {/* Action buttons — top right */}
          <div className="absolute top-3 right-3 flex flex-col gap-2 z-10">
            <button
              type="button"
              onClick={openZoom}
              className="w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-md hover:shadow-lg transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-brand-navy"
              aria-label="Zoom image"
            >
              <ZoomIn className="w-4 h-4 text-gray-700" strokeWidth={1.75} />
            </button>
            {productId && (
              <WishlistButton
                productId={productId}
                productTitle={productTitle}
                className="w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-md hover:shadow-lg transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-brand-navy"
              />
            )}
          </div>

          {/* Clickable main image */}
          <button
            type="button"
            onClick={openZoom}
            className="w-full h-full cursor-zoom-in outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-inset"
            aria-label="Click to zoom product image"
          >
            {selectedImage && (
              <Image
                src={selectedImage}
                alt={productTitle ?? "Product image"}
                width={600}
                height={600}
                className="w-full h-full object-contain p-6"
                priority
              />
            )}
          </button>
        </div>
      </div>

      {/* ── Zoom Lightbox ──────────────────────────────── */}
      {isMounted && isZoomOpen && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Product image zoom"
          className="fixed inset-0 z-fullscreen bg-white flex flex-col"
        >
          {/* Top bar */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-100">
            <p className="text-sm text-gray-500 font-medium truncate max-w-[70%]">
              {productTitle}
            </p>
            <div className="flex items-center gap-3">
              {imageUrls.length > 1 && (
                <span className="text-xs text-gray-400">
                  {selectedIndex + 1} / {imageUrls.length}
                </span>
              )}
              <button
                type="button"
                onClick={closeZoom}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand-navy"
                aria-label="Close zoom"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>

          {/* Zoomed image area */}
          <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-[#f8f8f8]">
            {/* Arrow navigation */}
            {imageUrls.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={prevImage}
                  className="absolute left-3 sm:left-5 z-10 w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md hover:shadow-lg transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-brand-navy"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <button
                  type="button"
                  onClick={nextImage}
                  className="absolute right-3 sm:right-5 z-10 w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md hover:shadow-lg transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-brand-navy"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </>
            )}

            {selectedImage && (
              <Image
                src={selectedImage}
                alt={productTitle ?? "Product image"}
                width={1200}
                height={1200}
                className="object-contain p-4 sm:p-8"
                style={{ maxWidth: "100%", maxHeight: "calc(100vh - 140px)" }}
                priority
              />
            )}
          </div>

          {/* Thumbnail strip at bottom */}
          {imageUrls.length > 1 && (
            <div className="flex-shrink-0 border-t border-gray-100 px-4 py-3 flex gap-2 overflow-x-auto justify-center">
              {imageUrls.map((img, idx) => (
                <button
                  key={img.id ?? idx}
                  type="button"
                  onClick={() => setSelectedIndex(idx)}
                  className={`
                    flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 bg-white transition-all
                    ${idx === selectedIndex
                      ? "border-brand-navy ring-1 ring-brand-navy"
                      : "border-gray-200 hover:border-gray-400"
                    }
                  `}
                  aria-label={`View image ${idx + 1}`}
                >
                  <Image
                    src={img.url!}
                    alt={`Image ${idx + 1}`}
                    width={56}
                    height={56}
                    className="w-full h-full object-contain p-0.5"
                  />
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  )
}

export default ImageGallery
